import type { RezaOpravneniDto } from '@gov-nx/api';
import type { LocaleDefinition } from '@gov-nx/core/types';
import { is } from '@gov-nx/core/types';
import { toDateReadable } from '@gov-nx/utils/common';
import type { RezaLocaleObject } from '../useRezaLocale';
import { zastupujiciOsobaNazevFO, zastupujiciOsobaNazevPO } from '../utils';
import type { RezaDetailContexts } from './Context';

export const getPPKeyValues = (
	data: RezaOpravneniDto,
	t: LocaleDefinition<RezaLocaleObject>['t']
): RezaDetailContexts['keyValues'] =>
	[
		{
			key: t('detail.table.kod-opravneni'),
			value: data.kod,
		},
		{
			key: t('detail.table.postoupeni.klic'),
			tooltip: { children: t('detail.table.postoupeni.tooltip') },
			value: data.seznamPostoupeni?.length
				? t('detail.table.postoupeni.ano')
				: t('detail.table.postoupeni.ne'),
		},
		{
			key: t('detail.table.platnost-od'),
			value: data?.platnostOd ? toDateReadable(data?.platnostOd) : undefined,
		},
		{
			key: t('detail.table.platnost-do'),
			value: data?.platnostDo
				? toDateReadable(data?.platnostDo)
				: t('sablony.detail.table.platnost-do-neomezeno'),
		},
		{
			key: t('detail.table.sablona'),
			value: data.nazevSablony,
		},
		{
			key: t('detail.table.postupitelny.klic'),
			tooltip: { children: t('detail.table.postupitelny.tooltip') },
			value: data?.postupitelny
				? t('detail.table.postupitelny.ano')
				: t('detail.table.postupitelny.ne'),
		},
		{
			key: t('detail.table.typ-zastoupeni.klic'),
			tooltip: { children: t('detail.table.typ-zastoupeni.tooltip') },

			value:
				data?.typ === 'explicitni'
					? t('detail.table.typ-zastoupeni.explicitni')
					: data?.typ === 'implicitni'
						? t('detail.table.typ-zastoupeni.implicitni')
						: undefined,
		},
	].filter(is);

export const getPOKeyValues = (
	data: RezaOpravneniDto,
	t: LocaleDefinition<RezaLocaleObject>['t']
): RezaDetailContexts['keyValues'] =>
	[
		data?.zastupujiciOsobaTyp === 'FO'
			? {
					key: t('detail.table.fo-jmeno'),
					value: data
						? zastupujiciOsobaNazevFO(data.zastupujiciOsoba)
						: undefined,
				}
			: undefined,
		data?.zastupujiciOsobaTyp === 'FO'
			? {
					key: t('detail.table.fo-datum-narozeni'),
					value: data?.zastupujiciOsoba?.fyzickaOsoba?.datumNarozeni
						? toDateReadable(
								data?.zastupujiciOsoba?.fyzickaOsoba?.datumNarozeni
							)
						: undefined,
				}
			: undefined,
		data?.zastupujiciOsobaTyp === 'FO'
			? {
					key: t('detail.table.fo-adresa'),
					value: data?.zastupujiciOsoba?.fyzickaOsoba?.adresa?.adresaText,
				}
			: undefined,
		data?.zastupujiciOsobaTyp === 'PO'
			? {
					key: t('detail.table.po-nazev'),
					value: data
						? zastupujiciOsobaNazevPO(data.zastupujiciOsoba)
						: undefined,
				}
			: undefined,
		data?.zastupujiciOsobaTyp === 'PO'
			? {
					key: t('detail.table.po-ico'),
					value: data.zastupujiciOsoba?.pravnickaOsoba?.ico,
				}
			: undefined,
		data?.zastupujiciOsobaTyp === 'PO'
			? {
					key: t('detail.table.po-sidlo'),
					value: data.zastupujiciOsoba?.pravnickaOsoba?.adresa?.adresaText,
				}
			: undefined,
		{
			key: t('detail.table.platnost-od'),
			value: data?.platnostOd ? toDateReadable(data?.platnostOd) : undefined,
		},
		{
			key: t('detail.table.platnost-do'),
			value: data?.platnostDo
				? toDateReadable(data?.platnostDo)
				: t('sablony.detail.table.platnost-do-neomezeno'),
		},
		{
			key: t('detail.table.identifikator'),
			value: data?.kod,
			canCopy: true,
		},
		{
			key: t('detail.table.postupitelny.klic'),
			value: data?.postupitelny
				? t('detail.table.postupitelny.ano')
				: t('detail.table.postupitelny.ne'),
		},
	].filter(is);
